import { Heading, Icon, Link, View } from '@adobe/react-spectrum';
import { List, ListItem } from '@react/react-spectrum/List';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Tooltip from '@react/react-spectrum/Tooltip';
import App from '@spectrum-icons/workflow/App';
import BranchCircle from '@spectrum-icons/workflow/BranchCircle';
import Download from '@spectrum-icons/workflow/Download';
import FileCode from '@spectrum-icons/workflow/FileCode';
import FileSpace from '@spectrum-icons/workflow/FileSpace';
import GlobeGrid from '@spectrum-icons/workflow/GlobeGrid';
import UploadToCloud from '@spectrum-icons/workflow/UploadToCloud';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { commonApi, handleApiCall } from '@exchange-frontends/api';
import { DynamicLengthListWithTooltip, WithSkeleton } from '@exchange-frontends/components';
import { useStaticData } from '@exchange-frontends/custom-hooks';
import { SAP_CODE_TO_ICON_ID, azSort, formatBytes, getProductNames } from '@exchange-frontends/utils';

import { dateFormatter } from '../../../utils/dateFormatter';

export const MetaInfoListItem = styled(ListItem)`
  && {
    cursor: default;
    padding: 0px;
    &:not(:last-child) {
      margin-bottom: var(--spectrum-global-dimension-size-200);
    }
    min-height: auto;
    &&:focus {
      background-color: rgba(0, 0, 0, 0);
    }
    &&:active {
      background-color: rgba(0, 0, 0, 0);
    }
    &&:hover {
      background-color: rgba(0, 0, 0, 0);
    }
  }
`;

const MetaListContainer = styled(List)`
  && {
    display: block;
    margin-top: ${({ $hasTopMargin }) => ($hasTopMargin ? '20px' : '0px')};
    margin-bottom: 0;
  }
`;

const MetaSectionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spectrum-global-dimension-size-200);
  box-sizing: border-box;
  max-height: var(--spectrum-global-dimension-size-800);
`;

const BlockText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  display: block;
  margin-top: 0;
  margin-bottom: ${({ $marginBottom }) => $marginBottom ?? 0};
`;

const ProductContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box; ;
`;

const ProductIcon = styled.svg`
  height: 24px;
  width: 24px;
`;
const ProductName = styled.span`
  margin-left: 8px;
  left: 5px;
  bottom: 4px;
`;

// Custom Hooks

const SidebarMetaInfo = ({
  optionalProducts,
  publishedDate,
  version,
  packageSize,
  languages,
  hostApps,
  categories,
  platforms,
  requiresInstallation,
  requiresSignIn,
  requiresPayment,
  licensing,
  metaInfoLoading,
  supportedProducts,
  isDownloadable,
  type,
  appType,
}) => {
  const [loading, setLoading] = useState(false);
  const [mappedOptionalProducts, setMappedOptionalProducts] = useState(null);
  const [mappedRequiredProducts, setMappedRequiredProducts] = useState(null);
  const [languageList, setLanguageList] = useState(null);
  const productsLocs = useStaticData('hostApps');
  const staticLicense = useStaticData('abdLicenseMap');
  const customLicense = licensing?.customLicenseUrl ?? licensing?.customUrl;
  const isABD = appType === 'ABD';

  //This check exists only for ZXP apps
  let requiresAppText = '';
  if (requiresInstallation) {
    if (platforms.length > 1) {
      requiresAppText = 'Requires additional app on your machine: Mac, Windows';
    } else {
      if (platforms[0].toLowerCase() === 'win') {
        requiresAppText = 'Requires additional app on your machine: Windows';
      } else if (platforms[0].toLowerCase() === 'mac') {
        requiresAppText = 'Requires additional app on your machine: Mac';
      }
    }
  }

  let requiresServiceText = '';
  let requiresServiceTooltipText = '';
  //This check exists for only UXP apps
  if (requiresSignIn && !requiresPayment) {
    requiresServiceText = 'Requires account with third-party service';
    requiresServiceTooltipText = "This plugin requires logging into the developer's website or service";
  } else if (requiresSignIn && requiresPayment) {
    requiresServiceText = 'Requires additional purchase to unlock all features';
    requiresServiceTooltipText = 'Some features may require payment to the developer';
  }

  const makeCCSupportedProductObjectsArray = (sapCodes, locs) => {
    sapCodes = sapCodes?.map((sapCode) => {
      return sapCode.hostAppCode;
    });
    const uniqueProductSAPCodes = Array.from(new Set(sapCodes));
    const productNames = getProductNames(uniqueProductSAPCodes, locs);
    return uniqueProductSAPCodes
      .map((key, idx) => ({ key, name: productNames[idx] }))
      .sort((a, b) => azSort(a.name, b.name));
  };

  // Fetch static Data
  useEffect(() => {
    if (optionalProducts?.length > 0 || hostApps?.length > 0) {
      let mounted = true;
      setLoading(true);
      const handler = handleApiCall(commonApi.staticData);
      handler
        .run()
        .then((response) => {
          if (mounted) {
            const productLocs = new Map(response.get('hostApps'));
            const optionalProductList = getProductNames(optionalProducts, productLocs).sort();
            const requiredProductList = getProductNames(hostApps, productLocs).sort();
            setMappedOptionalProducts(optionalProductList);
            setMappedRequiredProducts(requiredProductList);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
      return () => {
        handler.controller.abort();
        mounted = false;
      };
    }
  }, [optionalProducts, hostApps, categories]);

  //Fetch languages code for CC/EC apps
  useEffect(() => {
    if ((type === 'cc' || 'ec') && appType !== 'integrations') {
      let mounted = true;
      const handler = handleApiCall(commonApi.getLanguages);
      handler
        .run()
        .then((response) => {
          if (mounted) {
            setLanguageList(response.languages);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return () => {
        handler.controller.abort();
        mounted = false;
      };
    }
  }, [type, appType]);

  const CCSupportedProducts = makeCCSupportedProductObjectsArray(supportedProducts, productsLocs);

  // get icon label
  const getHostAppLabel = (hostAppCode) => {
    const CCSupportedProduct = CCSupportedProducts.find((supportedProduct) => supportedProduct.key === hostAppCode);
    return CCSupportedProduct?.name;
  };

  //Host apps with Icons
  let supportedApps = '';
  let filteredSupportedProducts = [];

  filteredSupportedProducts = Array.from(
    new Set(supportedProducts?.map((supportedProduct) => supportedProduct.hostAppCode))
  ).map((hostAppCode) => {
    return supportedProducts?.find((supportedProduct) => supportedProduct.hostAppCode === hostAppCode);
  });

  //@TODO: Move this to new component
  supportedApps = filteredSupportedProducts?.map((product) => (
    <MetaInfoListItem key={product.hostAppCode}>
      <ProductContainer key={product.hostAppCode}>
        <ProductIcon viewBox="0 0 240 234" data-testid="product-icon">
          <use xlinkHref={`#${SAP_CODE_TO_ICON_ID[product.hostAppCode]}`} />
        </ProductIcon>
        <ProductName>
          {getHostAppLabel(product.hostAppCode)}{' '}
          {product.minVersion + (product.maxVersion ? ' - ' + product.maxVersion : ' or higher')}
        </ProductName>
      </ProductContainer>
    </MetaInfoListItem>
  ));

  /**
   * @param {Array<string>} languages
   * @returns {Array<string>}
   */
  const getLanguages = (languages) => {
    const lowercasedLanguages = languages.map((e) => e.toLowerCase());
    if (!languageList) {
      return [...new Set(['en', ...lowercasedLanguages.sort(azSort)])];
    } else {
      /** @type {Array<string>} */
      const languageNames = languageList
        .filter((language) => lowercasedLanguages.includes(language.code.toLowerCase()))
        .map((language) => language.name);
      return [...new Set(['English', ...languageNames.sort(azSort)])];
    }
  };

  const getPlatforms = (supportedProducts) => {
    const platformsList = [
      ...new Set(
        supportedProducts?.map((supportedProduct) => {
          return supportedProduct?.type;
        })
      ),
    ];
    return platformsList;
  };

  return (
    <View
      backgroundColor="static-white"
      borderRadius="regular"
      padding="size-200"
      borderWidth="thin"
      borderColor="gray-200"
      marginBottom="size-400"
    >
      {supportedApps?.length > 0 && <View marginBottom="size-200">Compatible apps:</View>}
      {supportedApps}
      <WithSkeleton shouldRender={!metaInfoLoading && !loading} count={1}>
        {mappedRequiredProducts?.length > 0 && (
          <>
            <Heading level={4} marginTop="0" marginBottom="size-100">
              Required
            </Heading>
            <BlockText $marginBottom="16px">{mappedRequiredProducts.join(', ')}</BlockText>
          </>
        )}
      </WithSkeleton>

      <WithSkeleton shouldRender={!metaInfoLoading && !loading} count={1}>
        {mappedOptionalProducts?.length > 0 && (
          <>
            <Heading level={4} marginTop="0" marginBottom="size-100">
              Optional
            </Heading>
            <BlockText data-testid="supportedProduct-display">{mappedOptionalProducts.join(', ')}</BlockText>
          </>
        )}
      </WithSkeleton>
      <WithSkeleton shouldRender={!metaInfoLoading} count={7}>
        <MetaListContainer
          $hasTopMargin={supportedApps?.length || mappedOptionalProducts?.length || mappedRequiredProducts?.length}
          aria-label="meta information"
        >
          {isABD && (
            <MetaInfoListItem
              icon={
                <Icon size="S" marginEnd="size-100">
                  <svg viewBox="0 0 240 234">
                    <use xlinkHref={`#${SAP_CODE_TO_ICON_ID.ABD}`}></use>
                  </svg>
                </Icon>
              }
              data-testid="abd-app"
            >
              App Builder application
            </MetaInfoListItem>
          )}
          {publishedDate && (
            <MetaInfoListItem icon={<UploadToCloud size="S" marginEnd="size-100" />} data-testid="date-display">
              Published: {dateFormatter.format(new Date(publishedDate))}
            </MetaInfoListItem>
          )}
          {version && (
            <MetaInfoListItem icon={<FileCode size="S" marginEnd="size-100" />} data-testid="version-display">
              {`Version ${version}`}
            </MetaInfoListItem>
          )}
          {languages?.length > 0 && (
            <MetaInfoListItem icon={<GlobeGrid size="S" marginEnd="size-100" />}>
              <DynamicLengthListWithTooltip
                list={(type === 'cc' || 'ec') && appType !== 'integrations' ? getLanguages(languages) : languages}
                amountShown={1}
                natural
                listName={languages?.length > 2 ? 'languages' : 'language'}
                tooltipTriggerTextColor="inherit"
                tooltipVariant="inspect"
              />
            </MetaInfoListItem>
          )}
          {!!packageSize && (
            <MetaInfoListItem icon={<FileSpace size="S" marginEnd="size-100" />} data-testid="filesize-display">
              {formatBytes(packageSize)}
            </MetaInfoListItem>
          )}
          {requiresSignIn && (
            <OverlayTrigger placement="top">
              <MetaInfoListItem icon={<BranchCircle size="S" marginEnd="size-100" />}>
                {requiresServiceText}
              </MetaInfoListItem>
              <Tooltip variant="inspect">{requiresServiceTooltipText}</Tooltip>
            </OverlayTrigger>
          )}
          {requiresInstallation && (
            <OverlayTrigger placement="top">
              <MetaInfoListItem icon={<App size="S" marginEnd="size-100" />}>{requiresAppText}</MetaInfoListItem>
              <Tooltip variant="inspect">
                This plugin requires an app from the plugin developer to be installed on your device
              </Tooltip>
            </OverlayTrigger>
          )}
          {supportedProducts?.length > 0 && appType?.toLowerCase() === 'zxp' && (
            <MetaInfoListItem>Compatible: {getPlatforms(supportedProducts).sort(azSort).join(', ')}</MetaInfoListItem>
          )}
          {isDownloadable && (
            <MetaInfoListItem icon={<Download size="S" marginEnd="size-100" />}>
              Source Code Available for Download
            </MetaInfoListItem>
          )}
          {(customLicense || staticLicense?.[licensing?.license]) && (
            <MetaInfoListItem>
              {customLicense ? (
                <Link isQuiet>
                  <a href={customLicense} target="_blank" rel="noreferrer">
                    Custom
                  </a>
                </Link>
              ) : (
                staticLicense[licensing?.license]
              )}{' '}
              License type
            </MetaInfoListItem>
          )}
        </MetaListContainer>
      </WithSkeleton>
      {/* <Divider size="S" />
    <MetaSectionFooter>
      Free <Button variant="primary">Get</Button>
    </MetaSectionFooter> */}
    </View>
  );
};

export default SidebarMetaInfo;
